/**
 * The obligations, as a table of sentences (FR-E7).
 *
 * Shared by the Calendar screen (every client, a client column) and the client's Returns screen
 * (one client, no client column). Each row is `lib/calendar.ts`'s one sentence, with the state
 * beside it; a filed or locked return is rendered quiet — muted, no fee, no urgency — rather
 * than left out, so the list is the whole calendar and not only the alarming part.
 */

import { Link } from 'react-router'

import type { ObligationRecord } from '../lib/api'
import { isQuiet, nilBasisWords, obligationSentence, stateWords } from '../lib/calendar'
import { clientPath } from '../lib/clientScope'

export function ObligationList({
  rows,
  showClient,
}: {
  rows: ObligationRecord[]
  showClient: boolean
}) {
  if (rows.length === 0) {
    return <div className="gst-empty">Nothing on the calendar.</div>
  }
  return (
    <div className="gst-table-wrap">
      <table className="gst-table">
        <thead>
          <tr>
            {showClient && <th className="is-text">Client</th>}
            <th className="is-text">Obligation</th>
            <th className="is-text">State</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const quiet = isQuiet(row)
            const chip = quiet ? 'is-ok' : row.days > 0 ? 'is-stop' : 'is-hold'
            return (
              <tr
                key={`${row.client_id}-${row.return_type}-${row.period}`}
                className={`${i % 2 === 1 ? 'is-a' : ''} ${quiet ? 'opacity-60' : ''}`.trim()}
              >
                {showClient && (
                  <td className="is-text">
                    <Link className="gst-link" to={clientPath(row.client_id, 'returns')}>
                      {row.client_name}
                    </Link>
                  </td>
                )}
                <td className="is-text">
                  {obligationSentence(row)}
                  {/* Why the fee was computed at that rate — the basis the service answered
                      on, so an assumed rate is never read as a verified one (class D). */}
                  {!quiet && row.late_fee && (
                    <small className="block text-muted-foreground">
                      {nilBasisWords(row.nil_basis)}
                    </small>
                  )}
                </td>
                <td className="is-text">
                  <span className={`gst-chip ${chip}`}>{stateWords(row.state)}</span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
